import {Component, OnInit} from '@angular/core';
import {NgClass} from '@angular/common';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';

import {Observable, timer} from 'rxjs';

import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {DeviceDetectorService} from 'ngx-device-detector';

import {AUTHService} from 'finbricks-onebank-public-api/api/aUTH.service';
import {LoginRequest, CancelAuthRequest, Psu, ModelError} from 'finbricks-onebank-public-api/model/models';

interface RedirectResponse {
  /**
   * Redirect URI
   */
  redirectUri?: string;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    NgClass
  ],
  templateUrl: './login.component.html'
})
export class LoginComponent implements OnInit {

  checkoutForm = this.formBuilder.group({
    login: ['', Validators.required],
    password: ['', Validators.required]
  });

  protected bankTitle = '';
  private readonly bank365Title = '365.bank';
  private bank: string | undefined;
  private readonly bank365 = 'BANK365_SK';

  private authId: string | undefined;

  // It should be 5 minutes 1000 * 60 * 5
  private readonly delayForCancel = 1000 * 60 * 5;

  constructor(private translateService: TranslateService, private route: ActivatedRoute,
              private authService: AUTHService,
              private formBuilder: FormBuilder, private deviceService: DeviceDetectorService) {
    this.translateService.use('sk');
  }

  ngOnInit(): void {
    this.bank = this.route.snapshot.queryParams['bank'];
    if (this.bank && this.bank === this.bank365) {
      this.bankTitle = this.bank365Title;
    }
    this.authId = this.route.snapshot.queryParams['authId'];
    timer(this.delayForCancel).subscribe(() => this.cancel());
  }

  cancel() {
    this.processSubscibe(
      this.authService.cancelAuth({bank: this.bank, authId: this.authId} as CancelAuthRequest));
  }

  async onSubmit() {
    const xRequestId = crypto.randomUUID();

    let encryptedPassword: string = '';
    if (this.checkoutForm.value.password) {
      encryptedPassword = await this.encryptMessage(this.checkoutForm.value.password);
    }

    const loginRequest = {
      login: this.checkoutForm.value.login,
      password: encryptedPassword,
      authId: this.authId,
      bank: this.bank,
      psu: {
        userAgent: window.navigator.userAgent,
        ipAddress: location.host,
        ipPort: location.port,
        deviceOs: this.deviceService.os
      } as Psu
    } as LoginRequest;
    this.processSubscibe(this.authService.login(xRequestId, loginRequest));
  }

  private processSubscibe<T extends RedirectResponse>(response$: Observable<T>): void {
    response$.subscribe({
      next: value => {
        const redirectUri = value.redirectUri;
        if (redirectUri) {
          this.checkoutForm.reset();
          location.replace(redirectUri);
        }
      },
      error: err => {
        console.error(err);
        const title = ['Errors:', '---------'];
        const mappedEerrorCodeAndMessage = err.error.map((item: ModelError) => 'code: ' + item.code + '    message: ' + item.message);
        const codesAndMessages = [...title, mappedEerrorCodeAndMessage];
        alert(codesAndMessages.join('\n'));
      }
    });
  }

  private async encryptMessage(message: string): Promise<string> {
    const enc = new TextEncoder();
    const encoded = enc.encode(message);

    const secretKey = await this.importSecretKey();
    const ciphertext = await window.crypto.subtle.encrypt(
      {
        name: "RSA-OAEP",
      },
      secretKey,
      encoded
    );
    const decoder = new TextDecoder();
    const ciphertextStr = decoder.decode(ciphertext);
    console.log('ciphertextStr', ciphertextStr);

    const buffer = new Uint8Array(ciphertext);
    const bufferAsString = buffer.reduce((data, byte) => {
      return data + String.fromCharCode(byte);
    }, '');

    const btoaString = btoa(bufferAsString)
    console.log('btoaString', btoaString);
    return btoaString;
  }

  private importSecretKey(): Promise<CryptoKey> {
    const jwkEcKey = {
      "kty": "RSA",
      "alg": "RSA-OAEP-256",
      "n": "rmpBv8o4mO3wIuxAa2QJJia2j3WGJYJLy7GJxi6Ujnx_UrhQCZcjqKD1SjYYWw7bGWLL2b4UFme2ZckBpB_gv_TzP1gz4hq_hd5QtnkM2q6po0UD5kC7P5zsYFUZtB_PIsUoujTB-VvmvX1h6-0aTx8iamy8Wve2tAKacsNKFiPUOU-licFtxqB-SEFUfbJKn8bZMHUsF303CR_g9q0Y362O6IFcgksqjQN6JZhxXdE8HCfN2qiK_6grIP-wgxwoPR9ZF-fyqmDAPaZ2prtrEgEP5CpzclKpHKbifUfLilE-WC_SWc8lOI2p7RBNX4W26ZN4XxW1p4rAKiPNLR2yVw",
      "e": "AQAB"
    };
    return window.crypto.subtle.importKey(
      "jwk",
      jwkEcKey,
      {
        name: "RSA-OAEP",
        hash: "SHA-256",
      },
      false,
      ["encrypt"],
    );
  }
}
